import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBuyerPitchSession, submitBuyerPitchApplication, createBuyerPitchCheckout } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import NavHeader from '../components/NavHeader';
import { 
  Calendar, Users, Clock, Video, CheckCircle, 
  ArrowRight, Star, MessageSquare, Handshake, 
  PlayCircle, HelpCircle, ChevronDown, ChevronUp,
  Loader2, X, Play
} from 'lucide-react';

// Helper to convert Google Drive view URL to embed URL
const getEmbedUrl = (url) => {
  if (!url) return null;
  // Google Drive URLs
  if (url.includes('drive.google.com')) {
    // Extract file ID from various Google Drive URL formats
    const match = url.match(/\/d\/([^\/]+)/);
    if (match) {
      return `https://drive.google.com/file/d/${match[1]}/preview`;
    }
  }
  // YouTube URLs
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId[1]}`;
    }
  }
  // Vimeo URLs
  if (url.includes('vimeo.com')) {
    const vimeoId = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoId) {
      return `https://player.vimeo.com/video/${vimeoId[1]}`;
    }
  }
  // Return as-is for other URLs
  return url;
};

const BuyerPitchSession = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Video modal state
  const [showVideoModal, setShowVideoModal] = useState(false);
  
  // Application form state
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    startup_name: '',
    what_building: '',
    why_relevant: ''
  });
  
  // FAQ state
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    loadSession();
  }, [slug]);

  const loadSession = async () => {
    try {
      const data = await getBuyerPitchSession(slug);
      setSession(data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading session:', err);
      setError('Session not found');
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await submitBuyerPitchApplication(session._id, {
        session_id: session._id,
        ...formData
      });
      setApplicationId(result.application_id);
    } catch (err) {
      console.error('Error submitting application:', err);
      alert(err.response?.data?.detail || 'Error submitting application');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayment = async (packageType) => {
    setSubmitting(true);
    try {
      const originUrl = window.location.origin;
      const result = await createBuyerPitchCheckout(applicationId, packageType, originUrl);
      // Redirect to Stripe checkout
      window.location.href = result.checkout_url;
    } catch (err) {
      console.error('Error creating checkout:', err);
      alert(err.response?.data?.detail || 'Error processing payment');
      setSubmitting(false);
    }
  };

  const faqs = [
    {
      q: "What if I'm not selected?",
      a: "You won't be charged. We only collect payment after you're approved. If we later decide you're not a fit after payment, we'll refund you immediately."
    },
    {
      q: "Is this a sales pitch?",
      a: "This is feedback-focused, not a sales pitch. The buyer is here to give you honest feedback on your solution and messaging."
    },
    {
      q: "Can this lead to a customer?",
      a: "Yes, but it's not guaranteed. If there's a fit, the buyer may request a demo, intro, or even an advisory relationship."
    },
    {
      q: "What happens with the recording?",
      a: "You'll receive a shareable clip from your session. It's yours to use on LinkedIn, your website, or anywhere else."
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <NavHeader />
        <div className="max-w-4xl mx-auto px-6 py-24">
          <div className="text-center text-slate-400">Loading session...</div>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-slate-950">
        <NavHeader />
        <div className="max-w-4xl mx-auto px-6 py-24">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Session Not Found</h1>
            <p className="text-slate-400 mb-6">This session may no longer be available.</p>
            <Button onClick={() => navigate('/buyer-pitches')} className="bg-blue-600 hover:bg-blue-700">
              View All Sessions
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const spotsRemaining = session.max_spots - session.spots_filled;

  return (
    <div className="min-h-screen bg-slate-950">
      <NavHeader />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/10 via-transparent to-transparent" />
        <div className="max-w-4xl mx-auto px-6 relative">
          <div className="text-center">
            <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/30 mb-4">
              Buyer Pitch Session
            </Badge>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Pitch directly to the {session.role || 'Decision Maker'} at {session.company}
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
              Get real buyer feedback, a shareable LinkedIn clip, and a shot at your next customer.
            </p>
            
            {/* Session Details */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {session.recording_date && (
                <Badge variant="outline" className="border-slate-700 text-slate-300 px-4 py-2">
                  <Calendar className="w-4 h-4 mr-2" />
                  {formatDate(session.recording_date)}
                </Badge>
              )}
              {session.time_slot && (
                <Badge variant="outline" className="border-slate-700 text-slate-300 px-4 py-2">
                  <Clock className="w-4 h-4 mr-2" />
                  {session.time_slot}
                </Badge>
              )}
              <Badge variant="outline" className="border-amber-500/50 text-amber-400 px-4 py-2">
                <Users className="w-4 h-4 mr-2" />
                Limited to 5 founders
              </Badge>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg"
                onClick={() => setShowForm(true)}
                className="bg-blue-600 hover:bg-blue-700 text-lg px-8 py-6"
                data-testid="apply-cta-hero"
              >
                Apply to Pitch
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              
              {session.preview_video_url && (
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => setShowVideoModal(true)}
                  className="border-slate-600 text-white hover:bg-slate-800 text-lg px-8 py-6"
                  data-testid="watch-video-cta"
                >
                  <Play className="w-5 h-5 mr-2" />
                  See How to Pitch
                </Button>
              )}
            </div>
            
            {spotsRemaining <= 2 && spotsRemaining > 0 && (
              <p className="text-amber-400 text-sm mt-4">
                Only {spotsRemaining} spot{spotsRemaining > 1 ? 's' : ''} remaining!
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      {session.description && (
        <section className="max-w-4xl mx-auto px-6 pb-16">
          <Card className="bg-gradient-to-br from-blue-900/30 to-slate-900/50 border-blue-500/30">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-white mb-4">
                This is what {session.buyer_name} is actively trying to solve
              </h2>
              <div className="space-y-3 mb-6">
                {session.pain_points && session.pain_points.length > 0 ? (
                  session.pain_points.map((point, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                      <p className="text-slate-300">{point}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-400">
                    {session.description}
                  </p>
                )}
              </div>
              <p className="text-sm text-slate-400 border-t border-slate-700 pt-4">
                Founders aligned with these problems are most likely to get follow-ups.
              </p>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Buyer Section */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-8">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                {session.buyer_name?.charAt(0) || 'B'}
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">{session.buyer_name}</h3>
                {session.role && <p className="text-blue-400 font-medium">{session.role}</p>}
                <p className="text-slate-400">{session.company}</p>
                <p className="text-slate-500 text-sm mt-3">
                  This is a real buyer with budget influence, not a VC. They're evaluating solutions 
                  for their organization and can make purchasing decisions.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* What You Get Section */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <h2 className="text-2xl font-bold text-white mb-6 text-center">What You Get</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: PlayCircle, title: 'Recorded Clip', desc: 'A shareable video for LinkedIn and social proof' },
            { icon: MessageSquare, title: 'Actionable Feedback', desc: 'Direct, honest feedback from a real buyer' },
            { icon: Handshake, title: 'Potential Follow-ups', desc: 'Demo, intro, or advisory opportunities' },
          ].map((item, i) => (
            <Card key={i} className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-6 text-center">
                <item.icon className="w-10 h-10 text-blue-400 mx-auto mb-4" />
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <h2 className="text-2xl font-bold text-white mb-6 text-center">Pricing</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="bg-slate-900/50 border-slate-800 hover:border-blue-500/50 transition-all">
            <CardContent className="p-8 text-center">
              <Badge className="bg-slate-800 text-slate-300 mb-4">Pilot Pricing</Badge>
              <div className="text-4xl font-bold text-white mb-2">$150</div>
              <p className="text-slate-400 mb-4">Limited availability</p>
              <ul className="text-sm text-slate-400 space-y-2 text-left">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Full pitch session
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Video clip included
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Buyer feedback
                </li>
              </ul>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-900/30 to-slate-900/50 border-blue-500/30">
            <CardContent className="p-8 text-center">
              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 mb-4">Full Session</Badge>
              <div className="text-4xl font-bold text-white mb-2">$299</div>
              <p className="text-slate-400 mb-4">Standard rate</p>
              <ul className="text-sm text-slate-400 space-y-2 text-left">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Full pitch session
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Video clip included
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Buyer feedback
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
        <p className="text-center text-slate-500 text-sm mt-4">
          Only 5 founders selected per session. Pay after approval.
        </p>
      </section>

      {/* How It Works */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <h2 className="text-2xl font-bold text-white mb-6 text-center">How It Works</h2>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { step: 1, title: 'Apply', desc: 'Submit your application' },
            { step: 2, title: 'Get Approved', desc: 'We review for fit' },
            { step: 3, title: 'Pay to Confirm', desc: 'Secure your spot' },
            { step: 4, title: 'Pitch Live', desc: 'Present to the buyer' },
          ].map((item) => (
            <div key={item.step} className="text-center">
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center mx-auto mb-3">
                {item.step}
              </div>
              <h3 className="text-white font-semibold">{item.title}</h3>
              <p className="text-slate-400 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Session Details */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Session Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 text-slate-300">
              <Calendar className="w-5 h-5 text-blue-400" />
              <span>Recording Date: {formatDate(session.recording_date)}</span>
            </div>
            {session.time_slot && (
              <div className="flex items-center gap-3 text-slate-300">
                <Clock className="w-5 h-5 text-blue-400" />
                <span>Time Slot: {session.time_slot}</span>
              </div>
            )}
            <div className="flex items-center gap-3 text-slate-300">
              <Video className="w-5 h-5 text-blue-400" />
              <span>Format: Zoom Video Call</span>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <h2 className="text-2xl font-bold text-white mb-6 text-center">FAQ</h2>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <Card 
              key={i} 
              className="bg-slate-900/50 border-slate-800 cursor-pointer"
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-medium flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-blue-400" />
                    {faq.q}
                  </h3>
                  {openFaq === i ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
                {openFaq === i && (
                  <p className="text-slate-400 mt-3 pl-6">{faq.a}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-6 pb-24">
        <Card className="bg-gradient-to-br from-blue-900/30 to-slate-900/50 border-blue-500/30">
          <CardContent className="p-8 md:p-12 text-center">
            <Star className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              If you solve this problem, this is worth your time.
            </h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              Don't miss your chance to pitch directly to {session.buyer_name} and get 
              real feedback from a decision maker at {session.company}.
            </p>
            <Button 
              size="lg"
              onClick={() => setShowForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-lg px-8"
              data-testid="apply-cta-bottom"
            >
              Apply to Pitch
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </section>

      {/* Application Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="bg-slate-900 border-slate-700 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b border-slate-800">
              <CardTitle className="text-white flex items-center justify-between">
                {applicationId ? 'Choose Your Package' : 'Apply to Pitch'}
                <button 
                  onClick={() => {
                    setShowForm(false);
                    setApplicationId(null);
                  }}
                  className="text-slate-400 hover:text-white"
                >
                  ×
                </button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {!applicationId ? (
                <form onSubmit={handleSubmitApplication} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="apply-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="apply-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Startup Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.startup_name}
                      onChange={(e) => setFormData({...formData, startup_name: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="apply-startup-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">What are you building? *</label>
                    <textarea
                      required
                      rows={3}
                      value={formData.what_building}
                      onChange={(e) => setFormData({...formData, what_building: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="apply-building-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Why is this buyer relevant to you? *</label>
                    <textarea
                      required
                      rows={3}
                      value={formData.why_relevant}
                      onChange={(e) => setFormData({...formData, why_relevant: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="apply-relevant-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={submitting}
                    data-testid="submit-application-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Application'
                    )}
                  </Button>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="text-center mb-6">
                    <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-white">Application Submitted!</h3>
                    <p className="text-slate-400 mt-2">Choose your package to secure your spot.</p>
                  </div>
                  
                  <button
                    onClick={() => handlePayment('pilot')}
                    disabled={submitting}
                    className="w-full p-4 bg-slate-800 border border-slate-700 rounded-lg hover:border-blue-500 transition-all text-left"
                    data-testid="pay-pilot-btn"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-white font-semibold">Pilot Pricing</p>
                        <p className="text-slate-400 text-sm">Limited availability</p>
                      </div>
                      <div className="text-2xl font-bold text-white">$150</div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => handlePayment('full')}
                    disabled={submitting}
                    className="w-full p-4 bg-blue-900/30 border border-blue-500/30 rounded-lg hover:border-blue-500 transition-all text-left"
                    data-testid="pay-full-btn"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-white font-semibold">Full Session</p>
                        <p className="text-slate-400 text-sm">Standard rate</p>
                      </div>
                      <div className="text-2xl font-bold text-white">$299</div>
                    </div>
                  </button>
                  
                  {submitting && (
                    <div className="text-center text-slate-400">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Redirecting to payment...
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Video Preview Modal */}
      {showVideoModal && session.preview_video_url && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowVideoModal(false);
          }}
        >
          <div className="relative w-full max-w-4xl">
            {/* Close Button */}
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute -top-12 right-0 text-white/80 hover:text-white p-2 transition-colors"
              data-testid="close-video-modal"
            >
              <X className="w-8 h-8" />
            </button>
            
            {/* Video Container */}
            <div className="bg-slate-900 rounded-xl overflow-hidden shadow-2xl">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={getEmbedUrl(session.preview_video_url)}
                  className="absolute inset-0 w-full h-full"
                  allow="autoplay; encrypted-media; fullscreen"
                  allowFullScreen
                  title="How to Pitch Preview"
                />
              </div>
              
              {/* Modal Footer */}
              <div className="p-4 border-t border-slate-800">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-semibold">See How to Pitch</h3>
                    <p className="text-slate-400 text-sm">Learn what to expect in a buyer pitch session</p>
                  </div>
                  <Button 
                    onClick={() => {
                      setShowVideoModal(false);
                      setShowForm(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Apply Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <footer className="bg-slate-950 text-slate-400 py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm">&copy; {new Date().getFullYear()} The Founder Initiative. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BuyerPitchSession;
